import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Divider,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNotification } from '@ipc/ipc-components';
import { No3Cn56SummaryData, getDisabledFields } from './No3Cn56SummaryTable';
import { useNo3Cn56BilateralMutation, No3Cn56BilateralRequest } from '../hooks/useNo3Cn56BilateralMutation';

interface EditNo3Cn56ModalProps {
  open: boolean;
  onClose: () => void;
  data: No3Cn56SummaryData | null;
  onSave: (updatedData: No3Cn56SummaryData) => void;
  onShowSnackbar?: (message: string, severity: 'error' | 'warning' | 'info' | 'success') => void;
  fullData?: any; // Full data structure to find related XP/XG/XE rows
}

interface FormData {
  // Basic info (read-only)
  sendingPpo: string;
  receivingPpo: string;
  year: string;
  accountingType: string;
  service: string;
  mailCategory: string;
  format: string;
  product: string;
  
  // Editable bilateral fields
  cn56ItemsBilateral: string;
  cn56WeightBilateral: string;
  no3IpkBilateral: string;
  no3AdjBilateral: string;
  no3NetBilateral: string; // For non-XP/XG/XE formats
  no3NetP: string; // For XP format
  no3NetG: string; // For XG format
  no3NetE: string; // For XE format
  no3Cn54BisGrossIpkBilateral: string;
}

interface FormErrors {
  cn56ItemsBilateral?: string;
  cn56WeightBilateral?: string;
  no3IpkBilateral?: string;
  no3AdjBilateral?: string;
  no3NetBilateral?: string;
  no3NetP?: string;
  no3NetG?: string;
  no3NetE?: string;
  no3Cn54BisGrossIpkBilateral?: string;
}

export function EditNo3Cn56Modal({ open, onClose, data, onSave, onShowSnackbar, fullData }: EditNo3Cn56ModalProps) {
  const { successNotification, errorNotification } = useNotification();
  
  const [formData, setFormData] = useState<FormData>({
    sendingPpo: '',
    receivingPpo: '',
    year: '',
    accountingType: '',
    service: '',
    mailCategory: '',
    format: '',
    product: '',
    cn56ItemsBilateral: '',
    cn56WeightBilateral: '',
    no3IpkBilateral: '',
    no3AdjBilateral: '',
    no3NetBilateral: '',
    no3NetP: '',
    no3NetG: '',
    no3NetE: '',
    no3Cn54BisGrossIpkBilateral: '',
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Helper function to find related XP/XG/XE rows in the same group
  const findRelatedRows = (currentRow: No3Cn56SummaryData): { xpRow?: No3Cn56SummaryData; xgRow?: No3Cn56SummaryData; xeRow?: No3Cn56SummaryData } => {
    if (!fullData || !currentRow) return {};
    
    const direction = currentRow.direction || 'inbound';
    const dataSource = direction === 'inbound' 
      ? (fullData.inbound?.no3Cn56Summary || fullData.inbound)
      : (fullData.outbound?.no3Cn56Summary || fullData.outbound);
    
    if (!dataSource) return {};
    
    const relatedRows: { xpRow?: No3Cn56SummaryData; xgRow?: No3Cn56SummaryData; xeRow?: No3Cn56SummaryData } = {};
    console.log('relatedRows', relatedRows);
    
    // Find the group that contains the current row
    Object.values(dataSource).forEach((group: any) => {
      if (Array.isArray(group)) {
        group.forEach((row: any) => {
          // Check if this row is in the same group (same sendingPpo, receivingPpo, year, accountingType, mailCategory)
          if (
            row.sendingPpo === currentRow.sendingPpo &&
            row.receivingPpo === currentRow.receivingPpo &&
            row.year === currentRow.year &&
            row.accountingType === currentRow.accountingType &&
            row.mailCategory === currentRow.mailCategory && 
            row.service === currentRow.service && 
            row.product === currentRow.product
          ) {
            if (row.format === 'XP') {
              relatedRows.xpRow = { ...row, direction, rowIndex: group.indexOf(row) };
            } else if (row.format === 'XG') {
              relatedRows.xgRow = { ...row, direction, rowIndex: group.indexOf(row) };
            } else if (row.format === 'XE') {
              relatedRows.xeRow = { ...row, direction, rowIndex: group.indexOf(row) };
            }
          }
        });
      }
    });
    
    return relatedRows;
  };

  const bilateralMutation = useNo3Cn56BilateralMutation({
    onSuccess: () => {
      console.log('Mutation onSuccess called'); // Debug log
      
      // Try to show success notification, but don't let it break the flow
      try {
        successNotification('NO3 & CN56 bilateral data updated successfully');
      } catch (error) {
        console.warn('Failed to show success notification:', error);
        // Fallback to snackbar if notification fails
        if (onShowSnackbar) {
          onShowSnackbar('NO3 & CN56 bilateral data updated successfully', 'success');
        }
      }
      
      // Let the parent component handle closing the modal and refreshing data
      if (data && onSave) {
        console.log('Calling onSave with data:', data); // Debug log
        const updatedData: No3Cn56SummaryData = {
          ...data,
          cn56ItemsBilateral: formData.cn56ItemsBilateral,
          cn56WeightBilateral: formData.cn56WeightBilateral,
          no3IpkBilateral: formData.no3IpkBilateral,
          no3AdjBilateral: formData.no3AdjBilateral,
          no3NetBilateral: formData.no3NetBilateral,
          no3Cn54BisGrossIpkBilateral: formData.no3Cn54BisGrossIpkBilateral,
          rowIndex: data.rowIndex, // Preserve the row index
        };
        console.log('Calling onSave with updatedData:', updatedData); // Debug log
        
        try {
          onSave(updatedData);
          console.log('onSave called successfully'); // Debug log
        } catch (error) {
          console.error('Error calling onSave:', error);
          // Fallback notification
          if (onShowSnackbar) {
            onShowSnackbar('Record updated but failed to refresh data', 'warning');
          }
        }
      } else {
        console.log('onSave not called - data or onSave missing:', { data: !!data, onSave: !!onSave }); // Debug log
      }
    },
    onError: (error) => {
      console.error('API Error:', error);
      
      let errorMessage = 'Failed to update bilateral data';
      
      // Handle different error response structures
      if (error?.response?.data) {
        const errorData = error.response.data;
        
        // Check if errorData is directly an array
        if (Array.isArray(errorData)) {
          errorMessage = errorData.join(', ');
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.join(', ');
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Use snackbar if available, otherwise fall back to notification
      if (onShowSnackbar) {
        onShowSnackbar(errorMessage, 'error');
      } else {
        errorNotification(errorMessage);
      }
    }
  });

  // Helper function to format number with thousand separators (apostrophes)
  const formatWithThousandSeparator = (value: string, allowDecimals: boolean = false): string => {
    if (!value || value.trim() === '') return '';
    
    // Remove existing apostrophes for processing
    let cleaned = value.replace(/'/g, '');
    
    // Check if negative
    const isNegative = cleaned.startsWith('-');
    if (isNegative) {
      cleaned = cleaned.substring(1);
    }
    
    // Split by decimal point if decimals are allowed
    let integerPart = cleaned;
    let decimalPart = '';
    if (allowDecimals && cleaned.includes('.')) {
      const parts = cleaned.split('.');
      integerPart = parts[0];
      decimalPart = parts.slice(1).join('');
    }
    
    // Add thousand separators to integer part (from right to left, every 3 digits)
    if (integerPart) {
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
      return (isNegative ? '-' : '') + formattedInteger + (decimalPart ? '.' + decimalPart : '');
    }
    
    return (isNegative ? '-' : '') + cleaned;
  };

  // Update form data when data prop changes
  useEffect(() => {
    if (data) {
      const isXFormat = data.format === 'XP' || data.format === 'XG' || data.format === 'XE';
      
      if (isXFormat) {
        // Find related rows for XP/XG/XE formats
        const relatedRows = findRelatedRows(data);
        
        // Determine which Net% value to use for the current row
        let currentNetP = '';
        let currentNetG = '';
        let currentNetE = '';
        
        if (data.format === 'XP') {
          currentNetP = data.no3NetBilateral || '';
          currentNetG = relatedRows.xgRow?.no3NetBilateral || '';
          currentNetE = relatedRows.xeRow?.no3NetBilateral || '';
        } else if (data.format === 'XG') {
          currentNetP = relatedRows.xpRow?.no3NetBilateral || '';
          currentNetG = data.no3NetBilateral || '';
          currentNetE = relatedRows.xeRow?.no3NetBilateral || '';
        } else if (data.format === 'XE') {
          currentNetP = relatedRows.xpRow?.no3NetBilateral || '';
          currentNetG = relatedRows.xgRow?.no3NetBilateral || '';
          currentNetE = data.no3NetBilateral || '';
        }
        
        // Helper function to format value with thousand separators based on field type
        const formatValue = (val: string | number | null | undefined, fieldType: 'integer' | 'decimal' | 'decimal4' | 'percent'): string => {
          if (val === null || val === undefined || val === '') return '';
          const strVal = val.toString();
          if (strVal.trim() === '') return '';
          
          switch (fieldType) {
            case 'integer':
              return formatWithThousandSeparator(strVal);
            case 'decimal':
              return formatWithThousandSeparator(strVal, true);
            case 'decimal4':
              return formatWithThousandSeparator(strVal, true);
            case 'percent':
              return formatWithThousandSeparator(strVal, true);
            default:
              return strVal;
          }
        };
        
        setFormData({
          sendingPpo: data.sendingPpo || '',
          receivingPpo: data.receivingPpo || '',
          year: data.year || '',
          accountingType: data.accountingType || '',
          service: data.service || '',
          mailCategory: data.mailCategory || '',
          format: data.format || '',
          product: data.product || '',
          cn56ItemsBilateral: formatValue(data.cn56ItemsBilateral, 'integer'),
          cn56WeightBilateral: formatValue(data.cn56WeightBilateral, 'decimal'),
          no3IpkBilateral: formatValue(data.no3IpkBilateral, 'decimal4'),
          no3AdjBilateral: formatValue(data.no3AdjBilateral, 'decimal'),
          no3NetBilateral: '', // Not used for X formats
          no3NetP: formatValue(currentNetP, 'percent'),
          no3NetG: formatValue(currentNetG, 'percent'),
          no3NetE: formatValue(currentNetE, 'percent'),
          no3Cn54BisGrossIpkBilateral: formatValue(data.no3Cn54BisGrossIpkBilateral, 'decimal4'),
        });
      } else {
        // For non-X formats, use the standard field
        // Helper function to format value with thousand separators based on field type
        const formatValue = (val: string | number | null | undefined, fieldType: 'integer' | 'decimal' | 'decimal4' | 'percent'): string => {
          if (val === null || val === undefined || val === '') return '';
          const strVal = val.toString();
          if (strVal.trim() === '') return '';
          
          switch (fieldType) {
            case 'integer':
              return formatWithThousandSeparator(strVal);
            case 'decimal':
              return formatWithThousandSeparator(strVal, true);
            case 'decimal4':
              return formatWithThousandSeparator(strVal, true);
            case 'percent':
              return formatWithThousandSeparator(strVal, true);
            default:
              return strVal;
          }
        };
        
        setFormData({
          sendingPpo: data.sendingPpo || '',
          receivingPpo: data.receivingPpo || '',
          year: data.year || '',
          accountingType: data.accountingType || '',
          service: data.service || '',
          mailCategory: data.mailCategory || '',
          format: data.format || '',
          product: data.product || '',
          cn56ItemsBilateral: formatValue(data.cn56ItemsBilateral, 'integer'),
          cn56WeightBilateral: formatValue(data.cn56WeightBilateral, 'decimal'),
          no3IpkBilateral: formatValue(data.no3IpkBilateral, 'decimal4'),
          no3AdjBilateral: formatValue(data.no3AdjBilateral, 'decimal'),
          no3NetBilateral: formatValue(data.no3NetBilateral, 'percent'),
          no3NetP: '',
          no3NetG: '',
          no3NetE: '',
          no3Cn54BisGrossIpkBilateral: formatValue(data.no3Cn54BisGrossIpkBilateral, 'decimal4'),
        });
      }
    }
  }, [data, fullData]);

  // Validation and formatting functions
  const validateInteger = (value: string, fieldName: string): { value: string; error?: string } => {
    // Remove apostrophes temporarily for validation
    const withoutSeparators = value.replace(/'/g, '');
    
    // Check if the original value contained a decimal point
    if (withoutSeparators.includes('.')) {
      const cleaned = withoutSeparators.replace(/[^\d-]/g, '');
      return { value: formatWithThousandSeparator(cleaned), error: `${fieldName} must be an integer (no decimal places allowed)` };
    }
    
    // Allow digits, minus sign, and apostrophes
    // Remove invalid characters but keep apostrophes for now
    let cleaned = value.replace(/[^\d'-]/g, '');
    
    // Ensure only one minus sign at the beginning
    const hasMinus = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '');
    if (hasMinus) {
      cleaned = '-' + cleaned;
    }
    
    // Get value without separators for validation
    const withoutSeparatorsForValidation = cleaned.replace(/'/g, '');
    
    // Check for valid integer format (after removing separators)
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && !/^-?\d+$/.test(withoutSeparatorsForValidation)) {
      return { value: formatWithThousandSeparator(withoutSeparatorsForValidation), error: 'Invalid integer format' };
    }
    
    // Format with thousand separators if valid
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && /^-?\d+$/.test(withoutSeparatorsForValidation)) {
      cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation);
    }
    
    return { value: cleaned };
  };

  const validateDecimal = (value: string, requiredDecimals: number, fieldName: string): { value: string; error?: string } => {
    // Remove apostrophes temporarily for validation
    const withoutSeparators = value.replace(/'/g, '');
    
    // Allow digits, decimal point, minus sign, and apostrophes
    // Remove invalid characters but keep apostrophes for now
    let cleaned = value.replace(/[^\d.'-]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Ensure only one minus sign at the beginning
    const hasMinus = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '');
    if (hasMinus) {
      cleaned = '-' + cleaned;
    }
    
    // Get value without separators for validation
    const withoutSeparatorsForValidation = cleaned.replace(/'/g, '');
    
    // Check for valid decimal format (after removing separators)
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && !/^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
      return { value: formatWithThousandSeparator(withoutSeparatorsForValidation, true), error: 'Invalid decimal format' };
    }
    
    // Check if user is still typing (incomplete decimal)
    const decimalParts = withoutSeparatorsForValidation.split('.');
    const isTypingDecimal = decimalParts.length === 2 && decimalParts[1].length < requiredDecimals;
    const endsWithDecimalPoint = withoutSeparatorsForValidation.endsWith('.');
    
    // If value is not empty and not incomplete, check decimal places requirement
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && !isTypingDecimal && !endsWithDecimalPoint) {
      if (decimalParts.length === 2) {
        const decimalPlaces = decimalParts[1].length;
        if (decimalPlaces !== requiredDecimals) {
          return { 
            value: formatWithThousandSeparator(withoutSeparatorsForValidation, true), 
            error: `${fieldName} must have exactly ${requiredDecimals} decimal place${requiredDecimals > 1 ? 's' : ''}` 
          };
        }
      } else if (decimalParts.length === 1 && requiredDecimals > 0 && !withoutSeparatorsForValidation.endsWith('.')) {
        // Value has no decimal point but decimals are required (only show error if not typing)
        return { 
          value: formatWithThousandSeparator(withoutSeparatorsForValidation, true), 
          error: `${fieldName} must have exactly ${requiredDecimals} decimal place${requiredDecimals > 1 ? 's' : ''}` 
        };
      }
      
      // Format with thousand separators only if value is complete (has required decimals or is integer without decimals)
      if (/^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
        // Only format if decimal part is complete or doesn't exist
        if (decimalParts.length === 1 || (decimalParts.length === 2 && decimalParts[1].length === requiredDecimals)) {
          cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation, true);
        } else if (decimalParts.length === 2 && decimalParts[1].length > 0) {
          // Format integer part only, keep decimal part as-is while typing
          const formattedInteger = formatWithThousandSeparator(decimalParts[0] || '0', false);
          cleaned = formattedInteger + '.' + decimalParts[1];
          if (hasMinus && !cleaned.startsWith('-')) {
            cleaned = '-' + cleaned;
          }
        }
      }
    } else if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && /^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
      // While typing, format only the integer part before the decimal point
      if (decimalParts.length === 2) {
        const formattedInteger = formatWithThousandSeparator(decimalParts[0] || '0', false);
        cleaned = formattedInteger + '.' + decimalParts[1];
        if (hasMinus && !cleaned.startsWith('-')) {
          cleaned = '-' + cleaned;
        }
      } else if (decimalParts.length === 1 && !endsWithDecimalPoint) {
        // Format integer part if no decimal point
        cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation, false);
      }
    }
    
    return { value: cleaned };
  };

  const validateDecimalWithPercent = (value: string, requiredDecimals: number, fieldName: string): { value: string; error?: string } => {
    // Remove % sign for validation, but allow it in the display value
    const hasPercent = value.trim().endsWith('%');
    let valueWithoutPercent = hasPercent ? value.trim().slice(0, -1).trim() : value.trim();
    
    // Remove apostrophes temporarily for validation
    const withoutSeparators = valueWithoutPercent.replace(/'/g, '');
    
    // Allow digits, decimal point, minus sign, and apostrophes
    // Remove invalid characters but keep apostrophes for now
    let cleaned = valueWithoutPercent.replace(/[^\d.'-]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Ensure only one minus sign at the beginning
    const hasMinus = cleaned.startsWith('-');
    cleaned = cleaned.replace(/-/g, '');
    if (hasMinus) {
      cleaned = '-' + cleaned;
    }
    
    // Get value without separators for validation
    const withoutSeparatorsForValidation = cleaned.replace(/'/g, '');
    
    // Check for valid decimal format (after removing separators)
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && !/^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
      // This shouldn't happen after cleaning, but keep as safety check
      return { value: formatWithThousandSeparator(withoutSeparatorsForValidation, true) + (hasPercent ? '%' : '') };
    }
    
    // Check if user is still typing (incomplete decimal)
    const decimalParts = withoutSeparatorsForValidation.split('.');
    const isTypingDecimal = decimalParts.length === 2 && decimalParts[1].length < requiredDecimals;
    const endsWithDecimalPoint = withoutSeparatorsForValidation.endsWith('.');
    
    // If value is not empty and not incomplete, check decimal places requirement
    if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && !isTypingDecimal && !endsWithDecimalPoint) {
      if (decimalParts.length === 2) {
        const decimalPlaces = decimalParts[1].length;
        if (decimalPlaces !== requiredDecimals) {
          return { 
            value: formatWithThousandSeparator(withoutSeparatorsForValidation, true) + (hasPercent ? '%' : ''), 
            error: `${fieldName} must have exactly ${requiredDecimals} decimal place${requiredDecimals > 1 ? 's' : ''}` 
          };
        }
      } else if (decimalParts.length === 1 && requiredDecimals > 0 && !withoutSeparatorsForValidation.endsWith('.')) {
        // Value has no decimal point but decimals are required (only show error if not typing)
        return { 
          value: formatWithThousandSeparator(withoutSeparatorsForValidation, true) + (hasPercent ? '%' : ''), 
          error: `${fieldName} must have exactly ${requiredDecimals} decimal place${requiredDecimals > 1 ? 's' : ''}` 
        };
      }
      
      // Format with thousand separators only if value is complete (has required decimals or is integer without decimals)
      if (/^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
        // Only format if decimal part is complete or doesn't exist
        if (decimalParts.length === 1 || (decimalParts.length === 2 && decimalParts[1].length === requiredDecimals)) {
          cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation, true);
        } else if (decimalParts.length === 2 && decimalParts[1].length > 0) {
          // Format integer part only, keep decimal part as-is while typing
          const formattedInteger = formatWithThousandSeparator(decimalParts[0] || '0', false);
          cleaned = formattedInteger + '.' + decimalParts[1];
          if (hasMinus && !cleaned.startsWith('-')) {
            cleaned = '-' + cleaned;
          }
        }
      }
    } else if (withoutSeparatorsForValidation && withoutSeparatorsForValidation !== '-' && withoutSeparatorsForValidation !== '.' && /^-?\d*\.?\d*$/.test(withoutSeparatorsForValidation)) {
      // While typing, format only the integer part before the decimal point
      if (decimalParts.length === 2) {
        const formattedInteger = formatWithThousandSeparator(decimalParts[0] || '0', false);
        cleaned = formattedInteger + '.' + decimalParts[1];
        if (hasMinus && !cleaned.startsWith('-')) {
          cleaned = '-' + cleaned;
        }
      } else if (decimalParts.length === 1 && !endsWithDecimalPoint) {
        // Format integer part if no decimal point
        cleaned = formatWithThousandSeparator(withoutSeparatorsForValidation, false);
      }
    }
    
    // Return value with % sign if it was present
    return { value: cleaned + (hasPercent ? '%' : '') };
  };

  const formatForDisplay = (value: string, fieldType: 'integer' | 'decimal'): string => {
    if (!value || value.trim() === '') return '';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    
    switch (fieldType) {
      case 'integer':
        return Math.round(numValue).toString();
      case 'decimal':
        return numValue.toString();
      default:
        return value;
    }
  };

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = event.target.value;
    let error: string | undefined;
    
    // Apply validation based on field type
    switch (field) {
      case 'cn56ItemsBilateral':
        const intResult = validateInteger(value, 'CN56 Items');
        value = intResult.value;
        error = intResult.error;
        break;
      case 'cn56WeightBilateral':
        const weightResult = validateDecimal(value, 1, 'CN56 Weight'); // 1 decimal place
        value = weightResult.value;
        error = weightResult.error;
        break;
      case 'no3IpkBilateral':
        const ipkResult = validateDecimal(value, 4, 'NO3 NET IPK'); // 4 decimal places
        value = ipkResult.value;
        error = ipkResult.error;
        break;
      case 'no3AdjBilateral':
        const adjResult = validateDecimal(value, 2, 'NO3 ADJ'); // 2 decimal places
        value = adjResult.value;
        error = adjResult.error;
        break;
      case 'no3NetBilateral':
        const netResult = validateDecimalWithPercent(value, 2, 'NO3 Net%'); // 2 decimal places, allow %
        value = netResult.value;
        error = netResult.error;
        break;
      case 'no3NetP':
      case 'no3NetG':
      case 'no3NetE':
        const netXResult = validateDecimalWithPercent(value, 2, 'NO3 Net%'); // 2 decimal places, allow %
        value = netXResult.value;
        error = netXResult.error;
        break;
      case 'no3Cn54BisGrossIpkBilateral':
        const grossResult = validateDecimal(value, 4, 'NO3 GROSS IPK'); // 4 decimal places
        value = grossResult.value;
        error = grossResult.error;
        break;
      default:
        // No validation for other fields
        break;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Update errors
    setFormErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  // Helper function to clean and convert values to strings (as per Swagger schema)
  const cleanStringValue = (value: string | undefined | null): string => {
    if (!value || value.trim() === '') return '';
    
    // Remove thousand separators (apostrophes) and trim whitespace
    let cleaned = value.replace(/'/g, '').trim();
    
    // Remove % sign if present (for Net% fields)
    cleaned = cleaned.replace(/%/g, '').trim();
    
    // Handle negative numbers properly
    if (cleaned.startsWith('-')) {
      cleaned = '-' + cleaned.substring(1).replace(/^-/g, '');
    }
    
    // Ensure proper decimal formatting
    if (cleaned.includes('.')) {
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
    }
    
    return cleaned;
  };

  const handleSave = () => {
    if (!data) return;

    // Check for validation errors
    const hasErrors = Object.values(formErrors).some(error => !!error);
    if (hasErrors) {
      errorNotification('Please fix validation errors before saving');
      return;
    }

    // Basic validation
    if (!data.sendingPpo || !data.receivingPpo) {
      errorNotification('Sending PPO and Receiving PPO are required');
      return;
    }

    console.log('handleSave called with data:', data); // Debug log

    // Check if this is an XP/XG/XE format row
    const isXFormat = data.format === 'XP' || data.format === 'XG' || data.format === 'XE';
    
    // Create complete row object with all required fields as per Swagger schema
    const apiData: No3Cn56BilateralRequest = {
      // Required basic fields
      sendingPpo: data.sendingPpo.trim(),
      receivingPpo: data.receivingPpo.trim(),
      year: parseInt(data.year) || 0,
      accountingType: data.accountingType?.trim() || '',
      mailCategoryEnum: data.mailCategory?.trim() || 'A',
      rowType: (data.rowIndex || 0).toString(), // Use the row index as rowType
      
      // Original data fields (from existing record)
      cn56ItemsOriginal: cleanStringValue(data.cn56ItemsOriginal),
      cn56WeightOriginal: cleanStringValue(data.cn56WeightOriginal),
      no3IpkOriginal: cleanStringValue(data.no3IpkOriginal),
      no3GrossIpkOriginal: cleanStringValue(data.no3Cn54BisGrossIpkOriginal),
      
      // Bilateral data fields (user edited values)
      cn56Items: cleanStringValue(formData.cn56ItemsBilateral),
      cn56Weight: cleanStringValue(formData.cn56WeightBilateral),
      no3Ipk: cleanStringValue(formData.no3IpkBilateral),
      no3Adj: cleanStringValue(formData.no3AdjBilateral),
      no3GrossIpk: cleanStringValue(formData.no3Cn54BisGrossIpkBilateral),
      
      // Net% fields - for XP/XG/XE formats, send all three values
      no3NetP: isXFormat ? cleanStringValue(formData.no3NetP) : cleanStringValue(formData.no3NetBilateral),
      no3NetG: isXFormat ? cleanStringValue(formData.no3NetG) : '',
      no3NetE: isXFormat ? cleanStringValue(formData.no3NetE) : '',
    };

    console.log('Sending complete API data:', apiData); // Debug log
    bilateralMutation.mutate(apiData);
  };

  const handleCancel = () => {
    onClose();
  };

  // Memoize related rows to avoid recalculating on every render
  // Must be called before any early returns to follow Rules of Hooks
  const relatedRows = useMemo(() => {
    if (!data || (data.format !== 'XP' && data.format !== 'XG' && data.format !== 'XE')) {
      return {};
    }
    return findRelatedRows(data);
  }, [data, fullData]);

  // Get disabled fields for this record based on accounting type and row index
  // Must be called before any early returns to follow Rules of Hooks
  const disabledFields = useMemo(() => {
    if (!data) {
      return {
        cn56ItemsOriginal: false,
        cn56ItemsBilateral: false,
        cn56WeightOriginal: false,
        cn56WeightBilateral: false,
        no3IpkOriginal: false,
        no3IpkBilateral: false,
        no3AdjOriginal: false,
        no3AdjBilateral: false,
        no3NetOriginal: false,
        no3NetBilateral: false,
        no3Cn54BisGrossIpkOriginal: false,
        no3Cn54BisGrossIpkBilateral: false,
      };
    }
    return getDisabledFields(data.accountingType, data.rowIndex || 0);
  }, [data]);

  if (!data) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '70vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#E7EFF5',
          color: '#063142',
          fontWeight: 700,
          fontSize: '18px',
          borderBottom: '1px solid #ddd',
        }}
      >
        Edit NO3 & CN56 Data
        <IconButton
          onClick={onClose}
          sx={{
            color: '#063142',
            '&:hover': {
              backgroundColor: 'rgba(6, 49, 66, 0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }}>
            Record Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Sending PPO"
                value={formData.sendingPpo}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Receiving PPO"
                value={formData.receivingPpo}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Year"
                value={formData.year}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Accounting Type"
                value={formData.accountingType}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Service"
                value={formData.service}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Mail Category"
                value={formData.mailCategory}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Format"
                value={formData.format}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Product"
                value={formData.product}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ backgroundColor: '#f5f5f5' }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h6" sx={{ mb: 2, color: '#063142', fontWeight: 600 }}>
            Edit Bilateral Data
          </Typography>
          
          <Grid container spacing={3}>
            {/* CN56 Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#255e7c' }}>
                CN56
              </Typography>
            </Grid>
            
            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ pt: 1, pb: 1 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                      CN56 Items - Original
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="textPrimary">
                      {data.cn56ItemsOriginal || '-'}
                    </Typography>
                  </Box>
                </Grid>
                                 <Grid item xs={6}>
                   <TextField
                     label="CN56 Items - Bilateral"
                     value={formData.cn56ItemsBilateral}
                     onChange={handleInputChange('cn56ItemsBilateral')}
                     fullWidth
                     size="small"
                     disabled={disabledFields.cn56ItemsBilateral}
                     error={!!formErrors.cn56ItemsBilateral}
                     helperText={
                       disabledFields.cn56ItemsBilateral 
                         ? "Disabled field"
                         : formErrors.cn56ItemsBilateral || "Integer only"
                     }
                     inputProps={{
                       inputMode: 'numeric',
                       pattern: '[0-9-]*'
                     }}
                   
                   />
                 </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ pt: 1, pb: 1 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                      CN56 Weight - Original
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="textPrimary">
                      {data.cn56WeightOriginal || '-'}
                    </Typography>
                  </Box>
                </Grid>
                                 <Grid item xs={6}>
                   <TextField
                     label="CN56 Weight - Bilateral"
                     value={formData.cn56WeightBilateral}
                     onChange={handleInputChange('cn56WeightBilateral')}
                     fullWidth
                     size="small"
                     disabled={disabledFields.cn56WeightBilateral}
                     error={!!formErrors.cn56WeightBilateral}
                     helperText={
                       disabledFields.cn56WeightBilateral 
                         ? "Disabled field"
                         : formErrors.cn56WeightBilateral || "1 decimal required"
                     }
                     inputProps={{
                       inputMode: 'decimal',
                       step: '0.1'
                     }}
                   
                   />
                 </Grid>
              </Grid>
            </Grid>

            {/* NO3 Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 600, color: '#255e7c' }}>
                NO3
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ pt: 1, pb: 1 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                      NO3 NET IPK - Original
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="textPrimary">
                      {data.no3IpkOriginal || '-'}
                    </Typography>
                  </Box>
                </Grid>
                                 <Grid item xs={6}>
                   <TextField
                     label="NO3 NET IPK - Bilateral"
                     value={formData.no3IpkBilateral}
                     onChange={handleInputChange('no3IpkBilateral')}
                     fullWidth
                     size="small"
                     disabled={disabledFields.no3IpkBilateral}
                     error={!!formErrors.no3IpkBilateral}
                     helperText={
                       disabledFields.no3IpkBilateral 
                         ? "Disabled field"
                         : formErrors.no3IpkBilateral || "4 decimals required"
                     }
                     inputProps={{
                       inputMode: 'decimal',
                       step: '0.0001'
                     }}
                   
                   />
                 </Grid>
              </Grid>
            </Grid>

            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ pt: 1, pb: 1 }}>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                      NO3 ADJ - Original
                    </Typography>
                    <Typography variant="body1" fontWeight={500} color="textPrimary">
                      {data.no3AdjOriginal || '-'}
                    </Typography>
                  </Box>
                </Grid>
                                 <Grid item xs={6}>
                   <TextField
                     label="NO3 ADJ - Bilateral"
                     value={formData.no3AdjBilateral}
                     onChange={handleInputChange('no3AdjBilateral')}
                     fullWidth
                     size="small"
                     disabled={disabledFields.no3AdjBilateral}
                     error={!!formErrors.no3AdjBilateral}
                     helperText={
                       disabledFields.no3AdjBilateral 
                         ? "Disabled field"
                         : formErrors.no3AdjBilateral || "2 decimals required"
                     }
                     inputProps={{
                       inputMode: 'decimal',
                       step: '0.01'
                     }}
                   
                   />
                 </Grid>
              </Grid>
            </Grid>

            {/* NO3 Net% Section - Show different fields based on format */}
            {(data.format === 'XP' || data.format === 'XG' || data.format === 'XE') ? (
              <>
                {/* Show all three Net% fields for XP/XG/XE formats */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, mt: 1, fontWeight: 600, color: '#255e7c' }}>
                    NO3 Net% - XP / XG / XE Formats
                  </Typography>
                </Grid>
                
                {/* XP Net% */}
                <Grid item xs={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ pt: 1, pb: 1 }}>
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                          NO3 Net% XP - Original
                        </Typography>
                        <Typography variant="body1" fontWeight={500} color="textPrimary">
                          {relatedRows.xpRow?.no3NetOriginal || '-'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="NO3 Net% XP - Bilateral"
                        value={formData.no3NetP}
                        onChange={handleInputChange('no3NetP')}
                        fullWidth
                        size="small"
                        disabled={disabledFields.no3NetBilateral}
                        error={!!formErrors.no3NetP}
                        helperText={
                          disabledFields.no3NetBilateral 
                            ? "Disabled field"
                            : formErrors.no3NetP || "2 decimals required"
                        }
                        inputProps={{
                          inputMode: 'decimal',
                          step: '0.01'
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* XG Net% */}
                <Grid item xs={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ pt: 1, pb: 1 }}>
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                          NO3 Net% XG - Original
                        </Typography>
                        <Typography variant="body1" fontWeight={500} color="textPrimary">
                          {relatedRows.xgRow?.no3NetOriginal || '-'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="NO3 Net% XG - Bilateral"
                        value={formData.no3NetG}
                        onChange={handleInputChange('no3NetG')}
                        fullWidth
                        size="small"
                        disabled={disabledFields.no3NetBilateral}
                        error={!!formErrors.no3NetG}
                        helperText={
                          disabledFields.no3NetBilateral 
                            ? "Disabled field"
                            : formErrors.no3NetG || "2 decimals required"
                        }
                        inputProps={{
                          inputMode: 'decimal',
                          step: '0.01'
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* XE Net% */}
                <Grid item xs={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ pt: 1, pb: 1 }}>
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                          NO3 Net% XE - Original
                        </Typography>
                        <Typography variant="body1" fontWeight={500} color="textPrimary">
                          {relatedRows.xeRow?.no3NetOriginal || '-'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="NO3 Net% XE - Bilateral"
                        value={formData.no3NetE}
                        onChange={handleInputChange('no3NetE')}
                        fullWidth
                        size="small"
                        disabled={disabledFields.no3NetBilateral}
                        error={!!formErrors.no3NetE}
                        helperText={
                          disabledFields.no3NetBilateral 
                            ? "Disabled field"
                            : formErrors.no3NetE || "2 decimals required"
                        }
                        inputProps={{
                          inputMode: 'decimal',
                          step: '0.01'
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </>
            ) : (
              /* Show single Net% field for non-X formats */
              <Grid item xs={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ pt: 1, pb: 1 }}>
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 0.5 }}>
                        NO3 Net% - Original
                      </Typography>
                      <Typography variant="body1" fontWeight={500} color="textPrimary">
                        {data.no3NetOriginal || '-'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="NO3 Net% - Bilateral"
                      value={formData.no3NetBilateral}
                      onChange={handleInputChange('no3NetBilateral')}
                      fullWidth
                      size="small"
                      disabled={disabledFields.no3NetBilateral}
                      error={!!formErrors.no3NetBilateral}
                      helperText={
                        disabledFields.no3NetBilateral 
                          ? "Disabled field"
                          : formErrors.no3NetBilateral || "2 decimals required"
                      }
                      inputProps={{
                        inputMode: 'decimal',
                        step: '0.01'
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* NO3/CN54bis Section - Commented out for now */}
            {/*
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, mt: 2, fontWeight: 600, color: '#255e7c' }}>
                NO3/CN54bis
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="NO3 GROSS IPK - Original"
                    value={data.no3Cn54BisGrossIpkOriginal || ''}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    sx={{ backgroundColor: '#f5f5f5' }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="NO3 GROSS IPK - Bilateral"
                    value={formData.no3Cn54BisGrossIpkBilateral}
                    onChange={handleInputChange('no3Cn54BisGrossIpkBilateral')}
                    fullWidth
                    size="small"
                    disabled={disabledFields.no3Cn54BisGrossIpkBilateral}
                    error={!!formErrors.no3Cn54BisGrossIpkBilateral}
                    helperText={
                      disabledFields.no3Cn54BisGrossIpkBilateral 
                        ? "Disabled field"
                        : formErrors.no3Cn54BisGrossIpkBilateral || "4 decimals required"
                    }
                    inputProps={{
                      inputMode: 'decimal',
                      step: '0.0001'
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            */}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: '1px solid #ddd',
          backgroundColor: '#fafafa',
        }}
      >
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            color: '#063142',
            borderColor: '#063142',
            '&:hover': {
              borderColor: '#063142',
              backgroundColor: 'rgba(6, 49, 66, 0.05)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={bilateralMutation.isPending}
          startIcon={bilateralMutation.isPending ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            backgroundColor: '#255e7c',
            '&:hover': {
              backgroundColor: '#1e4a63',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
            },
          }}
        >
          {bilateralMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}